/**
 * @file 路由守卫：会话启动、登录态、强制改密、403。
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth, useAuthState } from '../auth/context';
import { AppShell, type NavItem } from './AppShell';
import logoUrl from '../../brand/logo.svg';

/**
 * @what 应用启动时恢复会话（只跑一次）
 * @return void 无返回值
 * @side 读 localStorage、可能调 /auth/refresh 与 /auth/me
 */
export function useAuthBootstrap(): void {
  const auth = useAuth();
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void auth.bootstrap();
  }, [auth]);
}

/**
 * @what 启动态占位（避免「闪一下登录页」）
 * @param props.endName 端名
 * @return JSX
 */
function BootSplash({ endName }: { endName: string }) {
  return (
    <div className="lx-splash">
      <img src={logoUrl} alt="" aria-hidden="true" />
      <span>{endName} 正在恢复会话…</span>
    </div>
  );
}

/**
 * @what 受保护布局：未登录跳登录页，必须改密时锁定到 /password
 * @param props.endName 端名
 * @param props.nav 导航项
 * @return JSX
 */
export function ProtectedLayout({ endName, nav }: { endName: string; nav: readonly NavItem[] }) {
  const state = useAuthState();
  const location = useLocation();

  if (state.phase === 'booting') return <BootSplash endName={endName} />;
  if (state.phase !== 'authenticated') {
    // 保留 query：联调参数（?devlogin / ?mockready）在跳转后仍可读，深链也不会丢筛选条件
    return <Navigate to={{ pathname: '/login', search: location.search }} replace state={{ from: location.pathname }} />;
  }
  if (state.identity?.must_change_password && location.pathname !== '/password') {
    return <Navigate to="/password" replace />;
  }

  return (
    <AppShell endName={endName} nav={nav}>
      <Outlet />
    </AppShell>
  );
}

/**
 * @what 单页权限守卫：无权限直接落到 /403（不做「假按钮」）
 * @param props.permission 权限码
 * @param props.children 页面
 * @return JSX
 */
export function PermissionRoute({ permission, children }: { permission: string; children: ReactNode }) {
  const state = useAuthState();
  const allowed = state.identity?.permissions?.includes(permission) ?? false;
  if (!allowed) return <Navigate to="/403" replace />;
  return <>{children}</>;
}
